import { NOT_SCORED_TEXT, type SpeechScores, weakSounds } from '../../shared/scoring.ts';
import { cn } from '../lib/cn.ts';

// Stimuler's bands: good above 70, neutral 50-70, needs work below 50.
const tone = (score: number) => (score > 70 ? 'text-good' : score >= 50 ? 'text-warn' : 'text-bad');

function Metric({ label, score, detail }: { label: string; score: number; detail?: string }) {
  return (
    <div className="rounded-xl bg-fg/[0.03] px-3 py-2 ring-1 ring-fg/5">
      <dt className="text-xs text-subtle">{label}</dt>
      <dd className={cn('text-lg font-semibold', tone(score))}>
        {score}
        {detail ? <span className="ml-1 text-xs font-normal text-subtle">{detail}</span> : null}
      </dd>
    </div>
  );
}

/** Delivery and language scores from Stimuler's speech analysis, under the content feedback. */
export function SpeechScoresView({ scores }: { scores: SpeechScores }) {
  if (scores.status === 'not_scored') {
    return (
      <p
        data-testid="speech-scores"
        data-status="not_scored"
        data-reason={scores.reason}
        className="text-sm text-subtle"
      >
        {NOT_SCORED_TEXT[scores.reason]}
      </p>
    );
  }
  const sounds = weakSounds(scores);
  const fix = scores.grammar?.edits[0];
  const word = scores.vocabulary?.suggestions[0];
  return (
    <section
      data-testid="speech-scores"
      data-status="scored"
      className="space-y-3 rounded-2xl bg-fg/[0.03] p-4 ring-1 ring-fg/10"
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-subtle">Speaking score</h4>
          <p className={cn('text-3xl font-semibold', tone(scores.overall))}>
            {scores.overall}
            <span className="text-base font-normal text-subtle">/100</span>
          </p>
        </div>
        {scores.cefr ? (
          <span
            title="CEFR level"
            className="rounded-full bg-accent/10 px-3 py-1 text-sm font-semibold text-accent ring-1 ring-accent/30"
          >
            {scores.cefr}
          </span>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {scores.pronunciation ? <Metric label="Pronunciation" score={scores.pronunciation.score} /> : null}
        {scores.fluency ? (
          <Metric
            label="Fluency"
            score={scores.fluency.score}
            detail={scores.fluency.wpm ? `${scores.fluency.wpm} wpm` : undefined}
          />
        ) : null}
        {scores.grammar ? <Metric label="Grammar" score={scores.grammar.score} /> : null}
        {scores.vocabulary ? <Metric label="Vocabulary" score={scores.vocabulary.score} /> : null}
      </dl>
      {sounds.length ? (
        <p className="flex flex-wrap items-center gap-1.5 text-sm text-muted">
          Sounds to practise:
          {sounds.map((p) => (
            <span key={p.phoneme} className="rounded-full bg-bad/10 px-2.5 py-0.5 font-medium text-bad">
              /{p.phoneme}/
            </span>
          ))}
        </p>
      ) : null}
      {fix ? (
        <p className="text-sm text-muted">
          {fix.category}: <span className="text-subtle line-through">{fix.original}</span>{' '}
          <span className="text-fg/85">{fix.corrected}</span>
        </p>
      ) : null}
      {word ? (
        <p className="text-sm text-muted">
          Word choice: <span className="text-subtle">{word.original}</span> →{' '}
          <span className="text-fg/85">{word.replacement}</span>
        </p>
      ) : null}
    </section>
  );
}
